import React, { useState, useEffect } from 'react';
import { managerApi } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Toaster, toast } from 'sonner';
import { 
  ArrowLeft, 
  Users,
  User,
  FileText,
  UserMinus,
  Archive,
  Calendar,
  ChevronRight,
  Home
} from 'lucide-react';
import TacticalEditor from './TacticalEditor';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ManagerDashboard = ({ subordinates: initialSubordinates, onBack }) => {
  const [subordinates, setSubordinates] = useState(initialSubordinates);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedTactical, setSelectedTactical] = useState(null);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [viewMode, setViewMode] = useState('team'); // 'team', 'employee-archives', 'tactical'
  
  // Archive navigation state for individual employee
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeArchives, setEmployeeArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const loadSubordinateTactical = async (userId) => {
    setLoading(true);
    try {
      const tactical = await managerApi.getSubordinateTactical(userId);
      if (tactical) {
        setSelectedTactical(tactical);
        setViewMode('tactical');
      } else {
        // No current tactical for this user
        toast.warning('This team member does not have a current tactical for this month. Check their archives.');
        setSelectedTactical(null);
      }
    } catch (err) {
      console.error('Failed to load tactical:', err);
      toast.error('Failed to load tactical');
      setSelectedTactical(null);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeArchives = async (userId) => {
    setLoadingArchives(true);
    try {
      const archives = await managerApi.getSubordinateArchives(userId);
      setEmployeeArchives(archives || []);
      
      // Group archives by year and month
      const grouped = {};
      for (const arch of (archives || [])) {
        const year = arch.year;
        const month = arch.month;
        if (!grouped[year]) {
          grouped[year] = {};
        }
        if (!grouped[year][month]) {
          grouped[year][month] = [];
        }
        grouped[year][month].push(arch);
      }
      setEmployeeArchives(archives || []);
    } catch (err) {
      console.error('Failed to load archives:', err);
      toast.error('Failed to load archives');
      setEmployeeArchives([]);
    } finally {
      setLoadingArchives(false);
    }
  };

  const handleSelectEmployeeForArchives = (sub) => {
    setSelectedEmployee(sub);
    setSelectedYear(null);
    setSelectedMonth(null);
    setViewMode('employee-archives');
    loadEmployeeArchives(sub.id);
  };

  const handleViewArchivedTactical = (tactical) => {
    setSelectedTactical(tactical);
    setViewMode('tactical');
  };

  const handleRemoveSubordinate = async (userId, displayName) => {
    if (!confirm(`Remove ${displayName} from your team? They will need to re-request access if needed.`)) {
      return;
    }
    
    setRemoving(userId);
    try {
      await managerApi.removeSubordinate(userId);
      setSubordinates(prev => prev.filter(s => s.id !== userId));
      if (selectedUserId === userId) {
        setSelectedUserId(null);
        setSelectedTactical(null);
      }
      toast.success(`${displayName} has been removed from your team`);
    } catch (err) {
      console.error('Failed to remove subordinate:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setRemoving(null);
    }
  };

  const handleBackFromTactical = () => {
    setSelectedTactical(null);
    if (selectedEmployee) {
      setViewMode('employee-archives');
    } else {
      setViewMode('team');
      setSelectedUserId(null);
    }
  };

  const handleArchiveBack = () => {
    if (selectedMonth !== null) {
      setSelectedMonth(null);
    } else if (selectedYear !== null) {
      setSelectedYear(null);
    } else {
      setSelectedEmployee(null);
      setViewMode('team');
    }
  };

  const handleGoHome = () => {
    setSelectedTactical(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedEmployee(null);
    setSelectedUserId(null);
    setViewMode('team');
  };

  // Get unique years from archives
  const getYears = () => {
    const years = [...new Set(employeeArchives.map(a => a.year))];
    return years.sort((a, b) => b - a); // Descending
  };

  // Get months for selected year
  const getMonths = () => {
    const months = [...new Set(
      employeeArchives
        .filter(a => a.year === selectedYear)
        .map(a => a.month)
    )];
    return months.sort((a, b) => b - a); // Descending
  };

  // Get tacticals for selected year and month
  const getTacticals = () => {
    return employeeArchives.filter(
      a => a.year === selectedYear && a.month === selectedMonth
    );
  };

  const selectedUser = subordinates.find(s => s.id === selectedUserId);

  // If viewing a tactical (current or archived)
  if (viewMode === 'tactical' && selectedTactical) {
    return (
      <TacticalEditor
        tactical={selectedTactical}
        onBack={handleBackFromTactical}
        onUpdate={(updated) => setSelectedTactical(updated)}
        isOwner={false}
      />
    );
  }

  // Employee Archives Browser
  if (viewMode === 'employee-archives' && selectedEmployee) {
    return (
      <div className="min-h-screen bg-background" data-testid="employee-archives">
        <Toaster position="top-right" />
        
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleArchiveBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button variant="ghost" size="sm" onClick={handleGoHome}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{selectedEmployee.display_name}'s Archives</span>
              {selectedYear && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span>{selectedYear}</span>
                </>
              )}
              {selectedMonth !== null && (
                <>
                  <ChevronRight className="w-4 h-4" />
                  <span>{MONTHS[selectedMonth - 1]}</span>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">{selectedEmployee.display_name}</h2>
            <p className="text-muted-foreground">{selectedEmployee.email}</p>
          </div>

          {loadingArchives ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : employeeArchives.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Archives Found</h3>
                <p className="text-muted-foreground">
                  {selectedEmployee.display_name} doesn't have any archived tacticals yet.
                </p>
              </CardContent>
            </Card>
          ) : !selectedYear ? (
            // Year Selection
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Year</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {getYears().map((year) => (
                  <Card 
                    key={year}
                    className="border-slate-200 hover:shadow-md cursor-pointer transition-all"
                    onClick={() => setSelectedYear(year)}
                  >
                    <CardContent className="py-6 text-center">
                      <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-2xl font-bold">{year}</p>
                      <p className="text-sm text-muted-foreground">
                        {employeeArchives.filter(a => a.year === year).length} tactical(s)
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : selectedMonth === null ? (
            // Month Selection
            <div>
              <h3 className="text-lg font-semibold mb-4">{selectedYear} - Select Month</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {getMonths().map((month) => (
                  <Card 
                    key={month}
                    className="border-slate-200 hover:shadow-md cursor-pointer transition-all"
                    onClick={() => setSelectedMonth(month)}
                  >
                    <CardContent className="py-6 text-center">
                      <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-xl font-bold">{MONTHS[month - 1]}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            // Tactical Selection for Selected Month
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {MONTHS[selectedMonth - 1]} {selectedYear}
              </h3>
              <div className="space-y-3">
                {getTacticals().map((tactical) => (
                  <Card 
                    key={tactical.id}
                    className="border-slate-200 hover:shadow-md cursor-pointer transition-all"
                    onClick={() => handleViewArchivedTactical(tactical)}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Archive className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {MONTHS[tactical.month - 1]} {tactical.year} Tactical
                            </p>
                            <p className="text-sm text-muted-foreground">Archived</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          View
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Main Team View
  return (
    <div className="min-h-screen bg-background" data-testid="manager-dashboard">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-semibold">Team Tacticals</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-2">Manage Team Members</h2>
          <p className="text-muted-foreground">
            View your team's current tacticals or browse their archives.
          </p>
        </div>

        {subordinates.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Team Members Yet</h3>
              <p className="text-muted-foreground">
                When team members invite you as their manager and you accept, they'll appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* View Current Tactical Selector */}
            <Card className="border-slate-200 mb-6">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  View Current Tactical
                </CardTitle>
                <CardDescription>
                  Select a team member to view their current month's tactical
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedUserId || ''}
                  onValueChange={(value) => {
                    setSelectedUserId(value);
                    loadSubordinateTactical(value);
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="team-member-select">
                    <SelectValue placeholder="Select a team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {subordinates.map((sub) => (
                      <SelectItem key={sub.id} value={sub.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{sub.display_name}</span>
                          <span className="text-muted-foreground">({sub.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Loading State */}
            {loading && (
              <Card className="border-slate-200 mb-6">
                <CardContent className="py-8">
                  <Skeleton className="h-8 w-48 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-3/4" />
                </CardContent>
              </Card>
            )}

            {/* Your Team's Archives */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Archive className="w-5 h-5 text-primary" />
                Your Team's Archives
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select a team member to browse their archived tacticals
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {subordinates.map((sub) => (
                  <Card 
                    key={sub.id} 
                    className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                    data-testid={`team-member-archive-card-${sub.id}`}
                    onClick={() => handleSelectEmployeeForArchives(sub)}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{sub.display_name}</p>
                            <p className="text-sm text-muted-foreground">{sub.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveSubordinate(sub.id, sub.display_name);
                            }}
                            disabled={removing === sub.id}
                            data-testid={`remove-subordinate-${sub.id}`}
                          >
                            <UserMinus className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ManagerDashboard;
